import {
  type ChannelMinFollowers,
  formatFollowerBoundary,
  type UpdateCommunitySettingsRequest,
} from '@inflip/contracts/admin';
import {
  SOCIAL_PLATFORM_LABELS,
  SPONSORSHIP_ELIGIBILITY_PLATFORMS,
  type SponsorshipEligibilityPlatform,
} from '@inflip/shared';
import { useEffect, useState } from 'react';

import { SavedAt, usePlatformSettings } from '../components/settings-form';
import { Button, ErrorBanner, LoadingRow, PageHeader, Section, inputCls } from '../components/ui';
import { api } from '../lib/api-client';

type Platform = SponsorshipEligibilityPlatform;

/** 폼 상태 — 입력 중 빈 칸을 허용하기 위해 경계값·기준 수치를 문자열로 든다 */
interface CommunityForm {
  boundaries: string[];
  post: Record<Platform, string>;
  comment: Record<Platform, string>;
}

const UNIT: Record<Platform, string> = {
  YOUTUBE: '구독자',
  INSTAGRAM: '팔로워',
  TIKTOK: '팔로워',
  THREADS: '팔로워',
  SOOP: '팔로워',
  PANDATV: '팔로워',
};

/** 채널별 값 하나씩으로 객체를 만든다 — 채널이 늘어도 폼 코드를 건드리지 않게 */
function perPlatform<T>(of: (platform: Platform) => T): Record<Platform, T> {
  return Object.fromEntries(SPONSORSHIP_ELIGIBILITY_PLATFORMS.map((p) => [p, of(p)])) as Record<
    Platform,
    T
  >;
}

const toCount = (value: string) => Math.max(0, Math.floor(Number(value)) || 0);
const toForm = (m: ChannelMinFollowers) => perPlatform((platform) => String(m[platform]));

/** 기준 안내 — "2만+" 또는 0 이면 "인증만" */
const describe = (value: string) => {
  const count = toCount(value);
  return count > 0 ? formatFollowerBoundary(count) : '인증만';
};

/** 커뮤니티 설정 — 팔로워 티어 표기 경계(2만+/5만+/…)와 채널별 글·댓글 작성 조건 */
export function CommunitySettingsPage() {
  const { session, settings, error, setError, run } = usePlatformSettings();
  const [form, setForm] = useState<CommunityForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (settings && !form) {
      const c = settings.community;
      setForm({
        boundaries: c.tierBoundaries.map(String),
        post: toForm(c.minFollowersToPostByPlatform),
        comment: toForm(c.minFollowersToCommentByPlatform),
      });
    }
  }, [settings, form]);

  if (!session) return null;

  const parsedBoundaries = form
    ? [...new Set(form.boundaries.map((v) => Number(v)).filter((n) => Number.isInteger(n) && n > 0))].sort(
        (a, b) => a - b,
      )
    : [];

  const save = async () => {
    if (!form || saving) return;
    if (parsedBoundaries.length === 0) {
      setError('티어 경계값을 1개 이상 입력해 주세요.');
      return;
    }
    const body: UpdateCommunitySettingsRequest = {
      tierBoundaries: parsedBoundaries,
      minFollowersToPostByPlatform: perPlatform((platform) => toCount(form.post[platform])),
      minFollowersToCommentByPlatform: perPlatform((platform) => toCount(form.comment[platform])),
    };
    setSaving(true);
    setSavedAt(null);
    const ok = await run(() => api.updateCommunitySettings(session.accessToken, body));
    if (ok) {
      setSavedAt(new Date());
      setForm({
        boundaries: body.tierBoundaries.map(String),
        post: toForm(body.minFollowersToPostByPlatform),
        comment: toForm(body.minFollowersToCommentByPlatform),
      });
    }
    setSaving(false);
  };

  const setBoundary = (index: number, value: string) =>
    form &&
    setForm({ ...form, boundaries: form.boundaries.map((v, i) => (i === index ? value : v)) });

  return (
    <main className="mx-auto w-full max-w-xl px-6 py-8">
      <PageHeader
        title="커뮤니티 설정"
        description="익명 커뮤니티의 팔로워 티어 표기와 활동 조건을 관리해요 — 저장 즉시 반영돼요"
        actions={
          <>
            <SavedAt at={savedAt} />
            <Button disabled={saving || !form} onClick={() => void save()}>
              {saving ? '저장 중…' : '저장'}
            </Button>
          </>
        }
      />
      {error && <ErrorBanner message={error} />}
      {!form && !error && <LoadingRow />}

      {form && (
        <>
          <Section title="팔로워 티어 경계">
            <p className="text-xs text-muted-foreground">
              글쓴이는 닉네임 없이 인증 플랫폼 아이콘과 티어 배지로만 표시돼요. 입력한 각 경계값 N 은
              &ldquo;N+&rdquo; 배지가 되고, 팔로워 수 이하 중 가장 큰 경계가 그 사람의 배지예요.
            </p>
            <div className="mt-3 space-y-2">
              {form.boundaries.map((value, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    className={`w-36 ${inputCls}`}
                    placeholder="팔로워 수"
                    value={value}
                    onChange={(e) => setBoundary(i, e.target.value)}
                  />
                  <span className="w-16 text-sm font-semibold text-muted-foreground">
                    {Number(value) > 0 ? formatFollowerBoundary(Number(value)) : '—'}
                  </span>
                  <button
                    type="button"
                    aria-label="경계 삭제"
                    onClick={() =>
                      setForm({ ...form, boundaries: form.boundaries.filter((_, j) => j !== i) })
                    }
                    className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-red-600"
                  >
                    <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 3l6 6M9 3l-6 6" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              className="mt-3"
              onClick={() => setForm({ ...form, boundaries: [...form.boundaries, ''] })}
            >
              경계 추가
            </Button>
            {parsedBoundaries.length > 0 && (
              <div className="mt-4 border-t border-border pt-3">
                <p className="text-xs font-semibold text-muted-foreground">표시 미리보기</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {parsedBoundaries.map((b) => (
                    <span
                      key={b}
                      className="rounded-sm bg-muted px-1.5 py-0.5 text-xs font-bold text-foreground"
                    >
                      {formatFollowerBoundary(b)}
                    </span>
                  ))}
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground/80">
                  저장 시 자동으로 오름차순 정렬되고 중복은 제거돼요.
                </p>
              </div>
            )}
          </Section>

          <Section title="활동 조건">
            <p className="text-xs text-muted-foreground">
              채널마다 글쓰기·댓글에 필요한 최소 구독자·팔로워 수예요. 인증된 채널 중 <b>하나만</b>{' '}
              그 채널 기준 이상이면 쓸 수 있어요. 0 이면 그 채널은 인증만 돼 있으면 돼요. 여러 채널의
              수치를 더하지 않아요.
            </p>
            <table className="mt-4 w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold text-muted-foreground">
                  <th className="pb-2 font-semibold">채널</th>
                  <th className="pb-2 font-semibold">글쓰기 최소</th>
                  <th className="pb-2 font-semibold">댓글 최소</th>
                </tr>
              </thead>
              <tbody>
                {SPONSORSHIP_ELIGIBILITY_PLATFORMS.map((platform) => (
                  <tr key={platform} className="align-top">
                    <td className="py-1.5 pr-3">
                      <p className="font-semibold">{SOCIAL_PLATFORM_LABELS[platform]}</p>
                      <p className="text-xs text-muted-foreground">{UNIT[platform]} 수</p>
                    </td>
                    {(['post', 'comment'] as const).map((kind) => (
                      <td key={kind} className="py-1.5 pr-3">
                        <input
                          type="number"
                          min={0}
                          aria-label={`${SOCIAL_PLATFORM_LABELS[platform]} ${kind === 'post' ? '글쓰기' : '댓글'} 최소 ${UNIT[platform]} 수`}
                          className={`w-32 ${inputCls}`}
                          value={form[kind][platform]}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              [kind]: { ...form[kind], [platform]: e.target.value },
                            })
                          }
                        />
                        <p className="mt-1 text-xs text-muted-foreground">
                          {describe(form[kind][platform])}
                        </p>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </>
      )}
    </main>
  );
}
